import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, completeProfilePath, accountPath, runningPath } from '../utils/paths';
import './PlayerRegistration.css';
import './AccountProfile.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api\/?$/, '') || 'http://localhost:8080';

const formatPosition = (p) => (p ? p.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '');
const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const formatSkillCategory = (c) => (c ? c.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (x) => x.toUpperCase()) : '—');

const emptySportForm = {
  position: 'MIDFIELDER',
  skillLevel: 5,
  skillCategory: '',
};

const PlayerProfile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [player, setPlayer] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(emptySportForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [playerData, accountData] = await Promise.all([
          api.getMyPlayerProfile(user.id),
          api.getAccountProfile(user.id).catch(() => null),
        ]);
        setPlayer(playerData);
        setAccount(accountData);
        setFormData({
          position: playerData.position || 'MIDFIELDER',
          skillLevel: playerData.skillLevel ?? 5,
          skillCategory: playerData.skillCategory || '',
        });
      } catch (err) {
        setPlayer(null);
        setError(err.message || 'Could not load profile. You may need to register as a player first.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'skillLevel' ? parseInt(value, 10) : value,
    }));
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!player || !user) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: account?.name || player.name,
        email: account?.email || player.email || user.email,
        phoneNumber: account?.phoneNumber || player.phoneNumber || '',
        dateOfBirth: account?.dateOfBirth || player.dateOfBirth || null,
        position: formData.position,
        skillLevel: formData.skillLevel,
        skillCategory: formData.skillCategory || null,
        ...(account?.profilePictureUrl && { profilePictureUrl: account.profilePictureUrl }),
      };
      const updated = await api.updatePlayer(player.id, payload, user.id);
      setPlayer(updated);
      setFormData({
        position: updated.position || 'MIDFIELDER',
        skillLevel: updated.skillLevel ?? 5,
        skillCategory: updated.skillCategory || '',
      });
      setSuccess('Soccer player profile updated.');
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      position: player?.position || 'MIDFIELDER',
      skillLevel: player?.skillLevel ?? 5,
      skillCategory: player?.skillCategory || '',
    });
    setError('');
    setEditing(false);
  };

  const profileImgUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="card">
        <p>Please log in to view your profile.</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate(soccerPath('/login'))}>
          Login
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="card">
        <h2>My Player Profile</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <p>You don&apos;t have a player profile yet. Complete your profile to join clubs and vote.</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate(completeProfilePath())}>
          Complete player profile
        </button>
      </div>
    );
  }

  const displayName = account?.name || player.name;
  const displayEmail = account?.email || player.email;
  const displayPhone = account?.phoneNumber || player.phoneNumber;
  const displayDob = account?.dateOfBirth || player.dateOfBirth;
  const displayPhoto = account?.profilePictureUrl || player.profilePictureUrl;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2>My soccer player profile</h2>
          <p className="form-help">Position, skill level, and club roster details.</p>
        </div>
        {!editing && (
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
            Edit soccer details
          </button>
        )}
      </div>

      <p className="account-profile-page__shared-note">
        Name, photo, phone, and password are managed on your{' '}
        <Link to={accountPath()}>account profile</Link>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!editing ? (
        <>
          <div className="profile-view">
            <div className="profile-view-header">
              {displayPhoto ? (
                <img
                  src={profileImgUrl(displayPhoto)}
                  alt={displayName}
                  className="profile-view-avatar"
                />
              ) : (
                <div className="profile-view-avatar-placeholder">{displayName?.charAt(0)?.toUpperCase() || '?'}</div>
              )}
              <div className="profile-view-title">
                <h3>{displayName}</h3>
                <p className="profile-view-status">{player.status?.replace(/_/g, ' ')}</p>
              </div>
            </div>
            <div className="profile-view-details">
              <p><strong>Email:</strong> {displayEmail}</p>
              {displayPhone && <p><strong>Phone:</strong> {displayPhone}</p>}
              {displayDob && <p><strong>Date of birth:</strong> {formatDate(displayDob)}</p>}
              <p><strong>Position:</strong> {formatPosition(player.position)}</p>
              <p><strong>Skill level:</strong> {player.skillLevel}/10</p>
              {player.skillCategory && <p><strong>Experience:</strong> {formatSkillCategory(player.skillCategory)}</p>}
            </div>
          </div>
          <p style={{ marginTop: '1.5rem' }}>
            Also run? <Link to={runningPath('/profile')}>Edit your running profile →</Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="profile-position">Playing position *</label>
            <select
              id="profile-position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
            >
              <option value="GOALKEEPER">Goalkeeper</option>
              <option value="DEFENDER">Defender</option>
              <option value="MIDFIELDER">Midfielder</option>
              <option value="FORWARD">Forward</option>
              <option value="WINGER">Winger</option>
              <option value="FLEXIBLE">Flexible (Multiple Positions)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="profile-skill">Skill level * (1–10): {formData.skillLevel}</label>
            <input
              type="range"
              id="profile-skill"
              name="skillLevel"
              min="1"
              max="10"
              value={formData.skillLevel}
              onChange={handleChange}
              required
            />
            <div className="skill-labels">
              <span>1 (Beginner)</span>
              <span>10 (Expert)</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="profile-skillCategory">Experience level (optional)</label>
            <select
              id="profile-skillCategory"
              name="skillCategory"
              value={formData.skillCategory}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem' }}
            >
              <option value="">— Select —</option>
              <option value="BEGINNER">Beginner</option>
              <option value="INTERMEDIATE">Intermediate</option>
              <option value="EXPERT">Expert</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PlayerProfile;
